import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Avatar, userColorVar } from '../shell/Avatar.tsx'
import { errorMessage } from '../../lib/api.ts'
import { useColumns, useTasks, useUpdateTask, useUsers } from '../../hooks/useTasks.ts'
import { useColumnWidths, minWidthFor } from '../../hooks/useColumnWidths.ts'
import { useHotkeys } from '../../hooks/useHotkeys.ts'
import { useMediaQuery } from '../../hooks/useMediaQuery.ts'
import { useRemoteChange } from '../../hooks/useRealtime.tsx'
import { EnterHighlight } from '../../ui/EnterHighlight.tsx'
import {
  CommentCount,
  DateCell,
  DueDate,
  ErrorState,
  LabelChip,
  PriorityMeter,
  Skeleton,
  StatusPill,
} from '../../ui/primitives.tsx'
import {
  PRIORITY_LABEL,
  type Column,
  type Priority,
  type Task,
  type User,
} from '../../lib/types.ts'
import { InlineCellMenu } from './InlineCellMenu.tsx'
import {
  EMPTY_FILTERS,
  TableToolbar,
  hasActiveFilters,
  type TableFiltersState,
} from './TableToolbar.tsx'
import { TaskCard } from './TaskCard.tsx'
import { TaskDrawer } from './TaskDrawer.tsx'
import styles from './table.module.css'

/**
 * One shared empty array, not a fresh `[]` per render.
 *
 * TanStack Table compares `data` by reference. While a query is loading — which is exactly what
 * happens the moment a filter changes — `tasks.data` is undefined, so `?? []` handed the table a
 * brand-new array on every render; the table rebuilt its row model, re-rendered, got another new
 * array, and the tab locked up hard enough for the browser to kill it.
 *
 * Any `?? []` feeding a memoised consumer is the same trap.
 */
const NO_TASKS: Task[] = []

/**
 * The second layout over the same data (PRD §9). No new endpoints, no new domain logic, and the
 * drawer is the board's drawer with no changes — if this view needed its own, stage 2 would have
 * kept something that belonged to the shared layer (RULES §7).
 *
 * Serves two tabs through one `scope` prop rather than two components: Tabela (`open`) and
 * Zamknięte (`closed`) differ by a single WHERE clause, and a copy would drift the moment either
 * one gained a column.
 */
export function TableView({ scope = 'open' }: { scope?: 'open' | 'closed' }): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<TableFiltersState>(() => ({
    ...EMPTY_FILTERS,
    q: searchParams.get('q') ?? '',
  }))
  const [sorting, setSorting] = useState<SortingState>([])
  const [openTaskId, setOpenTaskId] = useState<number | null>(null)

  // `?task=7` opens the drawer straight away, which is what a notification links to — and what
  // makes a task shareable at all. Cleared on close so the back button behaves.
  const linkedTask = Number(searchParams.get('task'))
  useEffect(() => {
    if (Number.isInteger(linkedTask) && linkedTask > 0) setOpenTaskId(linkedTask)
  }, [linkedTask])

  /**
   * `?q=` is where the top bar's search lands, so searching from anywhere means "show me this in
   * the table" rather than a second search implementation. An effect, not just the initial state:
   * searching again while already on /table changes the URL without remounting this component.
   */
  const linkedQuery = searchParams.get('q')
  useEffect(() => {
    if (linkedQuery !== null) setFilters((prev) => ({ ...prev, q: linkedQuery }))
  }, [linkedQuery])

  const closeDrawer = (): void => {
    setOpenTaskId(null)
    if (searchParams.has('task')) {
      const next = new URLSearchParams(searchParams)
      next.delete('task')
      setSearchParams(next, { replace: true })
    }
  }

  // Which columns fit is decided here rather than in media queries: the grid template is now built
  // from the stored widths, and CSS overriding it would give us two sources of truth for the same
  // number — the kind of split that ends with a layout nobody can explain.
  const narrow = useMediaQuery('(max-width: 700px)')
  const hideLabels = useMediaQuery('(max-width: 1100px)')
  const hidePerson = useMediaQuery('(max-width: 860px)')
  const hideCreated = useMediaQuery('(max-width: 980px)')

  const { widths, setWidth, resetWidth } = useColumnWidths()

  const hidden = useMemo(() => {
    const set = new Set<string>()
    if (hideLabels) set.add('labels')
    if (hidePerson) set.add('assignee')
    if (hideCreated) set.add('createdAt')
    return set
  }, [hideLabels, hidePerson, hideCreated])

  // Filtering server-side, so the counter tells the truth about the whole set rather than about
  // whatever happens to sit in the cache.
  const serverFilters = useMemo(
    () => ({
      scope,
      ...(filters.q ? { q: filters.q } : {}),
      ...(filters.assigneeId !== null ? { assigneeId: filters.assigneeId } : {}),
      ...(filters.label !== null ? { label: filters.label } : {}),
    }),
    [filters, scope],
  )

  const tasks = useTasks(serverFilters)
  const allForCount = useTasks(useMemo(() => ({ scope }), [scope]))
  const columns = useColumns()
  const users = useUsers()
  const updateTask = useUpdateTask()

  useHotkeys({ onEscape: closeDrawer, openTaskId })

  const rows = tasks.data ?? NO_TASKS
  const total = allForCount.data?.length ?? rows.length

  /**
   * One decision instead of `rows.length === 0` in two places.
   *
   * `rows` falls back to an empty array while the request is in flight, so testing its length alone
   * answered "is it empty?" with "yes" during every load and after every failure — the table said
   * "Nie ma jeszcze zadań" while it was fetching, and kept saying it when the network was down.
   * An outage looked exactly like an empty database.
   */
  const listState: 'error' | 'loading' | 'empty' | 'ready' = tasks.isError
    ? 'error'
    : tasks.isPending
      ? 'loading'
      : rows.length === 0
        ? 'empty'
        : 'ready'
  const knownLabels = useMemo(
    () => [...new Set((allForCount.data ?? []).flatMap((t) => t.labels))].sort(),
    [allForCount.data],
  )

  const tableColumns = useMemo(
    () =>
      buildColumns({
        columns: columns.data ?? [],
        users: users.data ?? [],
        onPatch: (id, patch) => updateTask.mutate({ id, patch }),
      }),
    [columns.data, users.data, updateTask],
  )

  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  /**
   * ui-spec §7 asks for this sentence by name and it did not exist anywhere in the code. Inline
   * edits invalidate on settle, so a rejected change snaps back on its own — silently, which reads
   * as "the click did not register" rather than "the server said no".
   */
  const saveFailed = updateTask.isError && (
    <ErrorState
      text={`Nie udało się zapisać zmiany. Zmiana została cofnięta. ${errorMessage(updateTask.error)}`}
      retryLabel="Ukryj"
      onRetry={() => updateTask.reset()}
    />
  )

  const toolbar = (
    <TableToolbar
      filters={filters}
      users={users.data ?? []}
      labels={knownLabels}
      shown={rows.length}
      total={total}
      onChange={setFilters}
    />
  )

  if (narrow) {
    return (
      <div className={styles.wrap}>
        {toolbar}
        {saveFailed}
        <div className={styles.cardList}>
          {listState === 'ready' &&
            rows.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                assignee={users.data?.find((u) => u.id === task.assigneeId) ?? null}
                onOpen={setOpenTaskId}
              />
            ))}
          {listState === 'loading' && <RowSkeletons />}
          {listState === 'error' && <ErrorState onRetry={() => void tasks.refetch()} />}
          {listState === 'empty' && (
            <NoResults filters={filters} total={total} scope={scope} onClear={setFilters} />
          )}
        </div>
        <TaskDrawer taskId={openTaskId} onClose={closeDrawer} />
      </div>
    )
  }

  const headers = (table.getHeaderGroups()[0]?.headers ?? []).filter(
    (header) => !hidden.has(header.column.id),
  )

  // Every column has a width of its own and the leftover space becomes one empty track at the end.
  // That trailing `1fr` is the whole fix for "the table is stretched to the right": without it the
  // title column absorbs the slack and a wide monitor turns into a wide title.
  const template = `${headers.map((h) => `${widths[h.column.id] ?? 120}px`).join(' ')} 1fr`

  return (
    <div className={styles.wrap}>
      {toolbar}
      {saveFailed}

      <div role="table" aria-label="Zadania" className={styles.scroller}>
        {/* role="rowgroup" wokół wierszy i nagłówka: `role="table"` wymaga tego pośredniego
            poziomu, a bez niego zwykły <div> między table a row zrywa relację ARIA i czytnik
            ekranu przestaje traktować to jako tabelę. */}
        <div className={styles.headGroup} role="rowgroup">
          <div
            className={`${styles.grid} ${styles.head}`}
            role="row"
            style={{ gridTemplateColumns: template }}
          >
            {headers.map((header) => {
              const sorted = header.column.getIsSorted()
              return (
                <div key={header.id} role="columnheader" className={styles.headSlot}>
                  <button
                    type="button"
                    className={styles.headCell}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {sorted && (
                      <span className={styles.sortMark}>{sorted === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </button>
                  <ResizeHandle
                    columnId={header.column.id}
                    width={widths[header.column.id] ?? 120}
                    onResize={setWidth}
                    onReset={resetWidth}
                  />
                </div>
              )
            })}
            <div aria-hidden="true" />
          </div>
        </div>

        <div className={styles.rows} role="rowgroup">
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} task={row.original} onOpen={setOpenTaskId} template={template}>
              {row
                .getVisibleCells()
                .filter((cell) => !hidden.has(cell.column.id))
                .map((cell) => (
                  <div key={cell.id} role="cell" className={styles.cellSlot}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              <div aria-hidden="true" />
            </TableRow>
          ))}
        </div>

        {listState === 'loading' && <RowSkeletons />}
        {listState === 'error' && <ErrorState onRetry={() => void tasks.refetch()} />}
        {listState === 'empty' && (
          <NoResults filters={filters} total={total} scope={scope} onClear={setFilters} />
        )}
      </div>

      <TaskDrawer taskId={openTaskId} onClose={closeDrawer} />
    </div>
  )
}

/**
 * Drag the right edge of a header to resize; double-click to go back to the default.
 *
 * Listeners go on `window`, not on the handle: during a drag the pointer spends most of its time
 * off this 9 px strip, and window listeners follow it without needing pointer capture (which throws
 * when the pointer id is not one the browser is tracking).
 *
 * Everything stops propagation, because the element behind the handle is the sort button and
 * resizing a column must not also re-sort the table.
 */
function ResizeHandle({
  columnId,
  width,
  onResize,
  onReset,
}: {
  columnId: string
  width: number
  onResize: (columnId: string, px: number) => void
  onReset: (columnId: string) => void
}): React.JSX.Element {
  const [dragging, setDragging] = useState(false)

  return (
    // A focusable separator is the WAI-ARIA window-splitter pattern: with tabIndex and
    // aria-valuenow it is a widget, not decoration. jsx-a11y treats `separator` as always
    // non-interactive, so both rules below are wrong here — and turning them off globally would
    // lose them everywhere they are right.
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
    <div
      className={`${styles.resizeHandle}${dragging ? ` ${styles.resizeHandleOn}` : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Zmień szerokość kolumny (min. ${minWidthFor(columnId)} px)`}
      aria-valuenow={Math.round(width)}
      aria-valuemin={minWidthFor(columnId)}
      // Focusable and driven by arrows. `role="separator"` with a pointer handler and no tabIndex
      // announces a resizer to a screen reader and then offers no way to reach it — resizing was
      // mouse-only while the markup claimed otherwise (ui-spec §11).
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onKeyDown={(e) => {
        const STEP = e.shiftKey ? 32 : 8
        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          onResize(columnId, width - STEP)
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          onResize(columnId, width + STEP)
        } else if (e.key === 'Home' || e.key === 'Enter') {
          // Same outcome as the double-click, reachable without a pointer.
          e.preventDefault()
          onReset(columnId)
        }
      }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation()
        onReset(columnId)
      }}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
        const startX = e.clientX
        const startWidth = width
        setDragging(true)

        const onMove = (move: PointerEvent): void =>
          onResize(columnId, startWidth + (move.clientX - startX))
        const onUp = (): void => {
          setDragging(false)
          // The cursor override goes on <body> so it stays col-resize while the pointer is
          // anywhere on screen — without it the cursor flickers back over every cell it crosses.
          document.body.style.removeProperty('cursor')
          document.body.style.removeProperty('user-select')
          window.removeEventListener('pointermove', onMove)
          window.removeEventListener('pointerup', onUp)
          window.removeEventListener('pointercancel', onUp)
        }

        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
        window.addEventListener('pointercancel', onUp)
      }}
    />
  )
}

function TableRow({
  task,
  onOpen,
  template,
  children,
}: {
  task: Task
  onOpen: (id: number) => void
  template: string
  children: React.ReactNode
}): React.JSX.Element {
  const flash = useRemoteChange('tasks', task.id)
  return (
    <div
      role="row"
      tabIndex={0}
      className={`${styles.grid} ${styles.row}`}
      style={{ position: 'relative', gridTemplateColumns: template }}
      onClick={() => onOpen(task.id)}
      onKeyDown={(e) => {
        // Space as well as Enter — anything that behaves like a button has to answer to both.
        // Guarded on the target so a key press inside an inline menu or a cell control does not
        // also open the drawer, and preventDefault stops Space from scrolling the page.
        if (e.key !== 'Enter' && e.key !== ' ') return
        if (e.target !== e.currentTarget) return
        e.preventDefault()
        onOpen(task.id)
      }}
    >
      <EnterHighlight active={flash} />
      {children}
    </div>
  )
}

/**
 * Loading placeholder shaped like the rows it replaces, with the staggered delays from ui-spec §7.
 * A row is 58 px here, which is what Skeleton defaults to — the point is that the page does not
 * change height when the data lands.
 */
function RowSkeletons(): React.JSX.Element {
  return (
    <>
      <Skeleton delay={0} />
      <Skeleton delay={200} />
      <Skeleton delay={400} />
    </>
  )
}

function NoResults({
  filters,
  total,
  scope,
  onClear,
}: {
  filters: TableFiltersState
  total: number
  scope: 'open' | 'closed'
  onClear: (next: TableFiltersState) => void
}): React.JSX.Element {
  if (!hasActiveFilters(filters)) {
    return (
      <div className={styles.empty}>
        <span className={styles.emptyText}>
          {scope === 'closed' ? 'Nic jeszcze nie zamknęliście' : 'Nie ma otwartych zadań'}
        </span>
      </div>
    )
  }

  return (
    <div className={styles.empty}>
      <span className={styles.emptyText}>Filtr obcina {total} zadań</span>
      <button type="button" className={styles.emptyAction} onClick={() => onClear(EMPTY_FILTERS)}>
        Wyczyść filtry
      </button>
    </div>
  )
}

function buildColumns({
  columns,
  users,
  onPatch,
}: {
  columns: Column[]
  users: User[]
  onPatch: (id: number, patch: { columnId?: number; priority?: Priority }) => void
}): ColumnDef<Task>[] {
  return [
    {
      id: 'ref',
      header: 'ID',
      accessorKey: 'id',
      cell: ({ row }) => <span className={styles.cellId}>{row.original.ref}</span>,
    },
    {
      id: 'title',
      header: 'Tytuł',
      accessorKey: 'title',
      cell: ({ row }) => <span className={styles.cellTitle}>{row.original.title}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (task) => columns.find((c) => c.id === task.columnId)?.position ?? 0,
      cell: ({ row }) => {
        const column = columns.find((c) => c.id === row.original.columnId)
        if (!column) return null
        return (
          <InlineCellMenu
            label={`Status zadania ${row.original.ref}`}
            trigger={<StatusPill column={column} interactive />}
            value={column.id}
            options={columns.map((c) => ({
              value: c.id,
              label: <StatusPill column={c} />,
            }))}
            onSelect={(columnId) => onPatch(row.original.id, { columnId: Number(columnId) })}
          />
        )
      },
    },
    {
      id: 'priority',
      header: 'Prio',
      accessorKey: 'priority',
      cell: ({ row }) => (
        <InlineCellMenu
          label={`Priorytet zadania ${row.original.ref}`}
          trigger={<PriorityMeter priority={row.original.priority} />}
          value={row.original.priority}
          options={([1, 2, 3] as Priority[]).map((p) => ({
            value: p,
            label: (
              <>
                <PriorityMeter priority={p} />
                {PRIORITY_LABEL[p]}
              </>
            ),
          }))}
          onSelect={(priority) =>
            onPatch(row.original.id, { priority: Number(priority) as Priority })
          }
        />
      ),
    },
    {
      id: 'assignee',
      header: 'Osoba',
      accessorFn: (task) => users.find((u) => u.id === task.assigneeId)?.displayName ?? '',
      cell: ({ row }) => {
        const user = users.find((u) => u.id === row.original.assigneeId)
        if (!user) return <span className={styles.muted}>—</span>
        return (
          <span className={styles.person}>
            <Avatar name={user.displayName} colorVar={userColorVar(user.id)} size={18} />
            <span className={styles.personName}>{user.displayName}</span>
          </span>
        )
      },
    },
    {
      id: 'createdAt',
      header: 'Dodano',
      accessorKey: 'createdAt',
      cell: ({ row }) => <DateCell date={row.original.createdAt} />,
    },
    {
      id: 'dueDate',
      header: 'Termin',
      // Tasks without a deadline sort last rather than first: an empty due date means "no date set",
      // not "due at the beginning of time".
      accessorFn: (task) => task.dueDate ?? '￿',
      cell: ({ row }) => <DueDate date={row.original.dueDate} />,
    },
    {
      id: 'labels',
      header: 'Labelki',
      enableSorting: false,
      cell: ({ row }) => (
        <span className={styles.cellLabels}>
          {row.original.labels.map((l) => (
            <LabelChip key={l}>{l}</LabelChip>
          ))}
        </span>
      ),
    },
    {
      id: 'comments',
      header: 'Kom.',
      accessorKey: 'commentCount',
      cell: ({ row }) => <CommentCount count={row.original.commentCount} />,
    },
  ]
}
